import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  CONFIGURATION_SETTING_KEYS,
  IJourneyTrip,
  ITariffRelationVia,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { ReconRouteGateway } from './recon-route.gateway';
import { ConfigurationStore } from '../../configuration/configuration-store.service';
import { ViasGateway } from '../../relations/vias/vias.gateway';
import { OperatorMappingStore } from '../../operator-mapping/operator-mapping-store.service';

interface IJourneyTripViaRequest extends IJourneyTrip {
  serviceAreaId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReconRouteStore {
  private readonly gateway: ReconRouteGateway = new ReconRouteGateway();
  private readonly viasGateway: ViasGateway = new ViasGateway();

  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );

  private readonly operatorMappingStore: OperatorMappingStore =
    inject(OperatorMappingStore);

  private readonly ctx: WritableSignal<string> = signal('');

  readonly #reconRouteResource: ResourceRef<
    IApiResponse<IJourneyTrip[]> | undefined
  > = resource({
    params: this.ctx,
    loader: ({
      params,
    }: ResourceLoaderParams<string>): Promise<
      IApiResponse<IJourneyTrip[]> | undefined
    > => {
      return params
        ? firstValueFrom(
            this.gateway.get({
              ctx: params,
              onDemandOperatorIds: this.operatorMappingStore
                .operatorMappings()
                .map(mapping => mapping.operatorId),
            })
          )
        : Promise.resolve(undefined);
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean => {
    return (
      this.#reconRouteResource.isLoading() || this.#viaResource.isLoading()
    );
  });

  readonly reconRoutes: Signal<IJourneyTrip[]> = computed(
    (): IJourneyTrip[] => {
      return this.#reconRouteResource.value()?.data ?? [];
    }
  );

  readonly reconRoute: Signal<IJourneyTrip> = computed((): IJourneyTrip => {
    return this.reconRoutes()[0];
  });

  readonly viaRequest: Signal<IJourneyTripViaRequest | undefined> = computed(
    () => {
      const reconRoute: IJourneyTrip = this.reconRoute();
      if (!reconRoute || reconRoute.tariffSegments !== undefined) {
        return undefined;
      }

      return {
        ...reconRoute,
        serviceAreaId: this.serviceAreaId(),
      };
    }
  );

  readonly #viaResource: ResourceRef<
    IApiResponse<ITariffRelationVia[]> | undefined
  > = resource({
    params: this.viaRequest,
    loader: ({
      params,
    }: ResourceLoaderParams<IJourneyTripViaRequest | undefined>): Promise<
      IApiResponse<ITariffRelationVia[]> | undefined
    > => {
      return params
        ? firstValueFrom(
            this.viasGateway.get({
              serviceAreaId: params.serviceAreaId,
              origin: params.origin.locationReferenceId,
              destination: params.destination.locationReferenceId,
            })
          )
        : Promise.resolve(undefined);
    },
  });

  readonly vias: Signal<ITariffRelationVia[]> = computed(
    (): ITariffRelationVia[] => {
      return this.#viaResource.value()?.data ?? [];
    }
  );

  readonly via: Signal<ITariffRelationVia | undefined> = computed(
    (): ITariffRelationVia | undefined => {
      return this.vias()[0];
    }
  );

  loadReconRoute(tileId: string): void {
    this.ctx.set(tileId);
  }

  reload(): void {
    this.#reconRouteResource.reload();
  }
}
